import React, { useState, useEffect } from 'react';
import { Code, FileText, Terminal, LayoutGrid, RefreshCw, AlertCircle, Database } from 'lucide-react';
import api from '../../api';
import toast from 'react-hot-toast';

interface CodeStats {
  overview: {
    totalFiles: number;
    totalLines: number;
    totalCode: number;
    totalComments: number;
    totalBlanks: number;
    totalComplexity: number;
    languageCount: number;
    databaseTables: number;
  };
  languages: Array<{
    name: string;
    files: number;
    lines: number;
    code: number;
    comments: number;
    blanks: number;
    complexity: number;
  }>;
  metrics: {
    commentRatio: string;
    avgComplexity: string;
    codePerFile: number;
  };
}

const CodeStatistics: React.FC = () => {
  const [stats, setStats] = useState<CodeStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [lastUpdated, setLastUpdated] = useState<string | null>(null);
  const [statsSource, setStatsSource] = useState<'live' | 'pregenerated'>('live');

  const fetchCodeStats = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await api.get('/api/code-stats');

      if (response.data.success) {
        setStats(response.data.data);
        setLastUpdated(new Date(response.data.timestamp).toLocaleString());
        setStatsSource(response.data.source || 'live');
      } else {
        setError(response.data.error || 'Failed to fetch code statistics');
      }
    } catch (err: any) {
      console.error('Error fetching code statistics:', err);
      setError(err.response?.data?.error || 'Failed to fetch code statistics');
      toast.error('Failed to fetch code statistics');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchCodeStats();
  }, []);

  const formatNumber = (num: number): string => {
    return num.toLocaleString();
  };

  const formatNumberShort = (num: number): string => {
    if (num >= 1000000) return `${(num / 1000000).toFixed(1)}M`;
    if (num >= 1000) return `${Math.round(num / 1000)}K`;
    return num.toString();
  };

  if (loading && !stats) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        <div className="flex items-center space-x-3 mb-4">
          <AlertCircle className="h-6 w-6 text-red-500" />
          <h2 className="text-xl font-bold text-gray-900">Code Statistics</h2>
        </div>
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <p className="text-red-800">{error}</p>
          {error.includes('not installed') && (
            <div className="mt-3 text-sm text-red-700">
              <p className="font-semibold">Installation Required:</p>
              <p className="mt-1">Please install SCC tool on the server:</p>
              <code className="block mt-2 bg-red-100 p-2 rounded">
                brew install scc (Mac) or download from https://github.com/boyter/scc
              </code>
            </div>
          )}
        </div>
        <button
          onClick={fetchCodeStats}
          className="mt-4 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 flex items-center space-x-2"
        >
          <RefreshCw className="h-4 w-4" />
          <span>Retry</span>
        </button>
      </div>
    );
  }

  if (!stats) {
    return null;
  }

  const jsCode = stats.languages.find(l => l.name === 'JavaScript')?.code || 0;
  const tsCode = stats.languages.find(l => l.name === 'TypeScript')?.code || 0;
  const jsTsPercentage = stats.overview.totalCode > 0
    ? Math.round(((jsCode + tsCode) / stats.overview.totalCode) * 100)
    : 0;

  const commentRatioValue = parseFloat(stats.metrics.commentRatio);
  const avgComplexityValue = parseFloat(stats.metrics.avgComplexity);
  const codePerFileValue = stats.metrics.codePerFile;

  // Helper function to calculate position on a scale
  const getPosition = (value: number, min: number, max: number): number => {
    const position = ((value - min) / (max - min)) * 100;
    return Math.max(0, Math.min(100, position));
  };

  // Helper function to calculate range bar position and width
  const getRangeStyle = (rangeMin: number, rangeMax: number, scaleMin: number, scaleMax: number) => {
    const left = getPosition(rangeMin, scaleMin, scaleMax);
    const right = getPosition(rangeMax, scaleMin, scaleMax);
    return { left: `${left}%`, width: `${right - left}%` };
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">Codebase Statistics</h2>
          <p className="text-gray-600 mt-1">
            Comprehensive analysis of the application's codebase
            <span className="ml-2 inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-green-100 text-green-800">
              Full Project
            </span>
          </p>
        </div>
        <div className="flex items-center space-x-4">
          {lastUpdated && (
            <span className="text-sm text-gray-500">
              Last updated: {lastUpdated}
            </span>
          )}
          <button
            onClick={fetchCodeStats}
            disabled={loading}
            className="flex items-center space-x-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
          >
            <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
            <span>Refresh</span>
          </button>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-5">
          <div className="flex items-center justify-between mb-3">
            <div className="p-2 bg-blue-100 rounded-lg">
              <Code className="h-5 w-5 text-blue-600" />
            </div>
          </div>
          <p className="text-sm font-medium text-gray-600">Lines of Code</p>
          <p className="text-2xl font-bold text-gray-900 mt-1">
            {formatNumberShort(stats.overview.totalCode)}
          </p>
          <div className="mt-3 p-2 bg-blue-50 border-l-4 border-blue-500 rounded-r">
            <p className="text-xs font-semibold text-blue-800">Insight:</p>
            <p className="text-xs text-blue-700 mt-1">
              Enterprise-scale platform. Typical mature apps range 100K-500K lines.
            </p>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-5">
          <div className="flex items-center justify-between mb-3">
            <div className="p-2 bg-green-100 rounded-lg">
              <FileText className="h-5 w-5 text-green-600" />
            </div>
          </div>
          <p className="text-sm font-medium text-gray-600">Total Files</p>
          <p className="text-2xl font-bold text-gray-900 mt-1">
            {formatNumber(stats.overview.totalFiles)}
          </p>
          <div className="mt-3 p-2 bg-green-50 border-l-4 border-green-500 rounded-r">
            <p className="text-xs font-semibold text-green-800">Insight:</p>
            <p className="text-xs text-green-700 mt-1">
              Modular architecture averaging {formatNumber(codePerFileValue)} lines per file.
            </p>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-5">
          <div className="flex items-center justify-between mb-3">
            <div className="p-2 bg-purple-100 rounded-lg">
              <Database className="h-5 w-5 text-purple-600" />
            </div>
          </div>
          <p className="text-sm font-medium text-gray-600">Database Tables</p>
          <p className="text-2xl font-bold text-gray-900 mt-1">
            {formatNumber(stats.overview.databaseTables)}
          </p>
          <div className="mt-3 p-2 bg-purple-50 border-l-4 border-purple-500 rounded-r">
            <p className="text-xs font-semibold text-purple-800">Insight:</p>
            <p className="text-xs text-purple-700 mt-1">
              Comprehensive data model for enterprise workflows and audit trails.
            </p>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-5">
          <div className="flex items-center justify-between mb-3">
            <div className="p-2 bg-cyan-100 rounded-lg">
              <Terminal className="h-5 w-5 text-cyan-600" />
            </div>
          </div>
          <p className="text-sm font-medium text-gray-600">Languages Used</p>
          <p className="text-2xl font-bold text-gray-900 mt-1">
            {stats.overview.languageCount}
          </p>
          <div className="mt-3 p-2 bg-cyan-50 border-l-4 border-cyan-500 rounded-r">
            <p className="text-xs font-semibold text-cyan-800">Insight:</p>
            <p className="text-xs text-cyan-700 mt-1">
              Modern stack with JS/TS comprising {jsTsPercentage}% of the codebase.
            </p>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-5">
          <div className="flex items-center justify-between mb-3">
            <div className="p-2 bg-orange-100 rounded-lg">
              <LayoutGrid className="h-5 w-5 text-orange-600" />
            </div>
          </div>
          <p className="text-sm font-medium text-gray-600">Decision Points</p>
          <p className="text-2xl font-bold text-gray-900 mt-1">
            {formatNumberShort(stats.overview.totalComplexity)}
          </p>
          <div className="mt-3 p-2 bg-orange-50 border-l-4 border-orange-500 rounded-r">
            <p className="text-xs font-semibold text-orange-800">Insight:</p>
            <p className="text-xs text-orange-700 mt-1">
              Sophisticated business logic for risk assessment and workflows.
            </p>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-6">Code Quality Metrics</h3>

          <div className="space-y-6">
            {/* Comment Ratio: Scale 0-30%, Ideal Range 10-20% */}
            <div>
              <div className="flex justify-between items-center mb-2">
                <span className="text-sm font-medium text-gray-700">Comment Ratio</span>
                <span className="text-lg font-bold text-gray-900">{stats.metrics.commentRatio}%</span>
              </div>
              <div className="relative w-full h-3 bg-gray-200 rounded-full overflow-hidden">
                {/* Green ideal range bar */}
                <div
                  className="absolute h-3 bg-green-200"
                  style={getRangeStyle(10, 20, 0, 30)}
                ></div>
                {/* Value indicator dot */}
                <div
                  className="absolute w-3 h-3 bg-green-600 rounded-full border-2 border-white shadow -translate-y-0"
                  style={{
                    left: `${getPosition(commentRatioValue, 0, 30)}%`,
                    transform: 'translateX(-50%)',
                    top: '0'
                  }}
                ></div>
              </div>
              <div className="flex justify-between mt-1 text-xs text-gray-500">
                <span>0%</span>
                <span className="text-green-600">Enterprise Range (10-20%)</span>
                <span>30%</span>
              </div>
            </div>

            {/* Avg Complexity: Scale 0-100, Ideal Range 20-60 */}
            <div>
              <div className="flex justify-between items-center mb-2">
                <span className="text-sm font-medium text-gray-700">Avg Complexity per File</span>
                <span className="text-lg font-bold text-gray-900">{stats.metrics.avgComplexity}</span>
              </div>
              <div className="relative w-full h-3 bg-gray-200 rounded-full overflow-hidden">
                {/* Green ideal range bar */}
                <div
                  className="absolute h-3 bg-green-200"
                  style={getRangeStyle(20, 60, 0, 100)}
                ></div>
                {/* Value indicator dot */}
                <div
                  className="absolute w-3 h-3 bg-green-600 rounded-full border-2 border-white shadow"
                  style={{
                    left: `${getPosition(avgComplexityValue, 0, 100)}%`,
                    transform: 'translateX(-50%)',
                    top: '0'
                  }}
                ></div>
              </div>
              <div className="flex justify-between mt-1 text-xs text-gray-500">
                <span>0</span>
                <span className="text-green-600">Optimal Range (20-60)</span>
                <span>100</span>
              </div>
              <div className="mt-3 p-3 bg-green-50 border-l-4 border-green-500 rounded-r">
                <p className="text-xs font-semibold text-green-800">Insight:</p>
                <p className="text-xs text-green-700 mt-1">
                  Well-balanced complexity indicating sophisticated business logic without over-engineering. Values above 60 typically require refactoring; JARVIS maintains optimal levels.
                </p>
              </div>
            </div>

            {/* Code per File: Scale 0-600, Ideal Range 200-500 */}
            <div>
              <div className="flex justify-between items-center mb-2">
                <span className="text-sm font-medium text-gray-700">Code per File</span>
                <span className="text-lg font-bold text-gray-900">{formatNumber(codePerFileValue)} lines</span>
              </div>
              <div className="relative w-full h-3 bg-gray-200 rounded-full overflow-hidden">
                {/* Green ideal range bar */}
                <div
                  className="absolute h-3 bg-green-200"
                  style={getRangeStyle(200, 500, 0, 600)}
                ></div>
                {/* Value indicator dot */}
                <div
                  className="absolute w-3 h-3 bg-green-600 rounded-full border-2 border-white shadow"
                  style={{
                    left: `${getPosition(codePerFileValue, 0, 600)}%`,
                    transform: 'translateX(-50%)',
                    top: '0'
                  }}
                ></div>
              </div>
              <div className="flex justify-between mt-1 text-xs text-gray-500">
                <span>0</span>
                <span className="text-green-600">Ideal Range (200-500 lines)</span>
                <span>600+</span>
              </div>
            </div>
          </div>
        </div>

        <div className="lg:col-span-2 bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Language Breakdown</h3>
          <div className="overflow-x-auto">
            <table className="min-w-full">
              <thead>
                <tr className="border-b border-gray-200">
                  <th className="text-left py-2 px-3 text-sm font-medium text-gray-600">Language</th>
                  <th className="text-right py-2 px-3 text-sm font-medium text-gray-600">Files</th>
                  <th className="text-right py-2 px-3 text-sm font-medium text-gray-600">Code</th>
                  <th className="text-right py-2 px-3 text-sm font-medium text-gray-600">Comments</th>
                  <th className="text-right py-2 px-3 text-sm font-medium text-gray-600">Complexity</th>
                </tr>
              </thead>
              <tbody>
                {stats.languages.map((lang, index) => (
                  <tr key={index} className="border-b border-gray-100 hover:bg-gray-50">
                    <td className="py-3 px-3 text-sm font-medium text-gray-900">{lang.name}</td>
                    <td className="py-3 px-3 text-sm text-gray-600 text-right">{formatNumber(lang.files)}</td>
                    <td className="py-3 px-3 text-sm text-gray-600 text-right">{formatNumber(lang.code)}</td>
                    <td className="py-3 px-3 text-sm text-gray-600 text-right">{formatNumber(lang.comments)}</td>
                    <td className="py-3 px-3 text-sm text-gray-600 text-right">{formatNumber(lang.complexity)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div className="mt-4 p-3 bg-blue-50 border-l-4 border-blue-500 rounded-r">
            <p className="text-xs font-semibold text-blue-800">Insight:</p>
            <p className="text-xs text-blue-700 mt-1">
              JavaScript and TypeScript represent {jsTsPercentage}% of the codebase, leveraging the same modern technologies used by Fortune 500 companies for mission-critical compliance and risk management platforms.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CodeStatistics;
